'use client';

import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { UsageChart } from '@/new_components/usage/UsageChart';
import { creditForecast } from '@/lib/credit-forecast';
import { LocaleLink } from '@/components/locale-link';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';

// ---------------------------------------------------------------------------
// Types — match backend /public/api/{sub}/usage response shape
// ---------------------------------------------------------------------------

interface SurfaceUsage {
  total: number;
  total_credits: number;
  by_key: Record<string, number>;
  by_credit: Record<string, number>;
}

interface ApiUsageDailyItem {
  date: string; // ISO date "YYYY-MM-DD"
  count: number;
  credit: number;
  by_endpoint: Record<string, number>;
  by_credit: Record<string, number>;
  v1: SurfaceUsage;
  mcp: SurfaceUsage;
}

interface ApiUsageResponse {
  sub: string;
  days: number;
  start_date: string;
  end_date: string;
  total: number;
  total_credits: number;
  by_endpoint_total: Record<string, number>;
  by_credit_total: Record<string, number>;
  v1: SurfaceUsage;
  mcp: SurfaceUsage;
  daily: ApiUsageDailyItem[];
  // Credit snapshot (not windowed): `balance` is what remains now, `allowance`
  // is the current period's granted amount. used = allowance - balance.
  balance: number;
  allowance: number;
}

const DAY_OPTIONS = [
  { value: '7', label: '7 days' },
  { value: '14', label: '14 days' },
  { value: '30', label: '30 days' },
  { value: '90', label: '90 days' },
];

/**
 * Credits spent in the last seven days, from the daily series already on the
 * page — no extra request. The window may be shorter than 7 days when the
 * user is looking at a 7-day range, which is fine: it is the same rate.
 */
function sevenDaySpend(daily: ApiUsageDailyItem[]): number {
  return daily.slice(-7).reduce((sum, day) => sum + (day.credit ?? 0), 0);
}

async function fetchUsage(days: number): Promise<ApiUsageResponse> {
  const res = await fetch(`/api/account/usage?days=${days}`);
  if (!res.ok) throw new Error('Failed to load usage');
  return res.json();
}

// ---------------------------------------------------------------------------
// Main content
// ---------------------------------------------------------------------------

/**
 * Remaining-credits bar. Shows how much of the current period's allowance is
 * used (used = allowance - balance) plus the remaining amount. Falls back to a
 * plain remaining count when there is no active allowance (allowance == 0).
 */
function CreditBalanceBar({
  balance,
  allowance,
  spent7d,
}: {
  balance: number;
  allowance: number;
  spent7d: number;
}) {
  const fmt = (n: number) => n.toLocaleString('de-DE');
  const forecast = creditForecast({ balance, spent7d });
  const remaining = Math.max(0, balance);
  const used = Math.max(0, allowance - balance);
  const pctUsed =
    allowance > 0 ? Math.min(100, Math.round((used / allowance) * 100)) : 0;
  const remainingRatio = allowance > 0 ? remaining / allowance : 1;

  // Matte fill: sage when healthy, mustard when low, rose when depleted —
  // same palette family as the charts so nothing screams for attention.
  const fill =
    remainingRatio <= 0
      ? '#b07a8a'
      : remainingRatio < 0.2
        ? '#c9a86a'
        : '#8fa876';

  return (
    <div className="border border-border p-5">
      <div className="flex items-baseline justify-between gap-3">
        <h3 className="text-sm font-semibold tracking-tight">Credit balance</h3>
        <span className="text-xs text-muted-foreground">
          {allowance > 0
            ? `${fmt(used)} / ${fmt(allowance)} used (${pctUsed}%)`
            : `${fmt(remaining)} credits`}
        </span>
      </div>
      <div className="mt-3 h-2.5 w-full overflow-hidden rounded-full bg-muted">
        <div
          className="h-full rounded-full transition-all"
          style={{
            width: `${allowance > 0 ? pctUsed : remaining > 0 ? 100 : 0}%`,
            backgroundColor: fill,
          }}
        />
      </div>
      <div className="mt-2 flex items-center justify-between text-xs text-muted-foreground">
        <span>
          <span className="font-medium text-foreground">{fmt(remaining)}</span>{' '}
          remaining
        </span>
        {allowance > 0 && <span>{fmt(allowance)} / period</span>}
      </div>
      {/* A balance is a number; a forecast is a decision. Shown only when
          there is real spending to extrapolate from. */}
      {forecast && (
        <div className="mt-3 flex flex-wrap items-center justify-between gap-2 border-t border-border pt-3 text-xs">
          <span className={forecast.urgent ? 'text-amber-600' : 'text-muted-foreground'}>
            About <span className="font-medium">{forecast.daysLeft} days</span>{' '}
            left at your current rate ({fmt(Math.round(forecast.perDay))} / day
            over the last week).
          </span>
          {forecast.urgent && (
            <LocaleLink
              href="/plans"
              className="font-medium text-amber-700 underline underline-offset-2 hover:text-amber-800"
            >
              Top up
            </LocaleLink>
          )}
        </div>
      )}
    </div>
  );
}

export function AccountUsageContent() {
  const [days, setDays] = useState(7);

  const { data, isLoading, isError } = useQuery({
    queryKey: ['account-usage', days],
    queryFn: () => fetchUsage(days),
  });

  return (
    <div className="flex flex-col gap-5 mt-5 lg:mt-7.5">
      {/* Header */}
      <div className="flex items-center justify-between border border-border p-5">
        <div>
          <h2 className="text-lg font-semibold tracking-tight">API Usage</h2>
          <p className="text-sm text-muted-foreground mt-0.5">
            Daily request and credit consumption across the v1 API and MCP tools
            used by your keys.
          </p>
        </div>
        <Select value={String(days)} onValueChange={(v) => setDays(Number(v))}>
          <SelectTrigger className="w-32 h-9 text-xs">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {DAY_OPTIONS.map((o) => (
              <SelectItem key={o.value} value={o.value}>
                {o.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {data && (data.allowance > 0 || data.balance > 0) && (
        <CreditBalanceBar
          balance={data.balance ?? 0}
          allowance={data.allowance ?? 0}
          spent7d={sevenDaySpend(data.daily ?? [])}
        />
      )}

      {isError && (
        <div className="border border-border p-5 text-sm text-destructive">
          Failed to load usage data.
        </div>
      )}

      <UsageChart
        title="Requests & credits"
        days={days}
        daily={data?.daily ?? []}
        byEndpointTotal={data?.by_endpoint_total ?? {}}
        byCreditTotal={data?.by_credit_total ?? {}}
        total={data?.total ?? 0}
        totalCredits={data?.total_credits ?? 0}
        isLoading={isLoading}
        footnote={
          data && (data.v1.total > 0 || data.mcp.total > 0) ? (
            <>
              v1 {data.v1.total.toLocaleString('de-DE')} req /{' '}
              {data.v1.total_credits.toLocaleString('de-DE')} cr · MCP{' '}
              {data.mcp.total.toLocaleString('de-DE')} req /{' '}
              {data.mcp.total_credits.toLocaleString('de-DE')} cr
            </>
          ) : null
        }
      />
    </div>
  );
}
